import { EtiquetaDePagina, CLASE_TITULO_DE_PAGINA } from '@/components/patterns';
import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { AppLayout } from '@/components/layout/app-layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import { useToast } from '@/lib/hooks/use-toast';
import { contractService } from '../services/contract.service';
import type { Contract, ContractType } from '../types/contract.types';

const TYPE_OPTIONS: { value: ContractType; label: string }[] = [
  { value: 'CONTRACT', label: 'Contrato' },
  { value: 'TERMS', label: 'Términos y Condiciones' },
  { value: 'PRIVACY', label: 'Política de Privacidad' },
  { value: 'AMENDMENT', label: 'Adenda / Modificación' },
  { value: 'OTHER', label: 'Otro' },
];

export function ContractEditPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [type, setType] = useState<ContractType>('CONTRACT');
  const [content, setContent] = useState('');
  const [isTemplate, setIsTemplate] = useState(searchParams.get('template') === 'true');
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    contractService
      .getById(id)
      .then((c: Contract) => {
        setName(c.name);
        setType(c.type);
        setContent(c.content);
        setIsTemplate(c.isTemplate);
        setClientName(c.clientName || '');
        setClientEmail(c.clientEmail || '');
        setValidFrom(c.validFrom ? c.validFrom.slice(0, 10) : '');
        setValidTo(c.validTo ? c.validTo.slice(0, 10) : '');
      })
      .catch(() =>
        toast({
          title: 'Error',
          description: 'No se pudo cargar el contrato',
          variant: 'destructive',
        })
      )
      .finally(() => setLoading(false));
  }, [id, toast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      toast({ title: 'Error', description: 'El nombre es requerido', variant: 'destructive' });
      return;
    }
    if (!content.trim()) {
      toast({ title: 'Error', description: 'El contenido es requerido', variant: 'destructive' });
      return;
    }

    try {
      setSaving(true);
      const payload = {
        name: name.trim(),
        type,
        content,
        isTemplate,
        clientName: clientName.trim() || null,
        clientEmail: clientEmail.trim() || null,
        validFrom: validFrom ? new Date(validFrom).toISOString() : null,
        validTo: validTo ? new Date(validTo).toISOString() : null,
      };

      let result: Contract;
      if (isEditing && id) {
        result = await contractService.update(id, payload);
        toast({ title: 'Contrato actualizado' });
      } else {
        result = await contractService.create(payload);
        toast({ title: 'Contrato creado' });
      }
      navigate(`/contracts/${result.id}`);
    } catch {
      toast({
        title: 'Error',
        description: 'No se pudo guardar el contrato',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6 p-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(isEditing ? `/contracts/${id}` : '/contracts')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="mb-3">
            <EtiquetaDePagina modulo="cotiza">Cotiza · Gestión</EtiquetaDePagina>
          </div>
          <h1 className={CLASE_TITULO_DE_PAGINA}>
            {isEditing ? 'Editar Contrato' : isTemplate ? 'Nueva Plantilla' : 'Nuevo Contrato'}
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* General */}
          <Card className="border-white/10">
            <CardHeader>
              <CardTitle className="text-lg">Información General</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nombre *</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Ej: Contrato de Servicio 2026"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="type">Tipo</Label>
                  <Select value={type} onValueChange={(v) => setType(v as ContractType)}>
                    <SelectTrigger id="type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TYPE_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="isTemplate"
                  checked={isTemplate}
                  onCheckedChange={(checked) => setIsTemplate(checked === true)}
                />
                <Label htmlFor="isTemplate" className="text-sm cursor-pointer">
                  Es plantilla reutilizable
                </Label>
              </div>
            </CardContent>
          </Card>

          {/* Client Info */}
          {!isTemplate && (
            <Card className="border-white/10">
              <CardHeader>
                <CardTitle className="text-lg">Datos del Cliente</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="clientName">Nombre del Cliente</Label>
                    <Input
                      id="clientName"
                      value={clientName}
                      onChange={(e) => setClientName(e.target.value)}
                      placeholder="Nombre de la empresa o persona"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="clientEmail">Email del Cliente</Label>
                    <Input
                      id="clientEmail"
                      type="email"
                      value={clientEmail}
                      onChange={(e) => setClientEmail(e.target.value)}
                      placeholder="cliente@empresa.com"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Validity */}
          <Card className="border-white/10">
            <CardHeader>
              <CardTitle className="text-lg">Vigencia</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="validFrom">Válido desde</Label>
                  <Input
                    id="validFrom"
                    type="date"
                    value={validFrom}
                    onChange={(e) => setValidFrom(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="validTo">Válido hasta</Label>
                  <Input
                    id="validTo"
                    type="date"
                    value={validTo}
                    onChange={(e) => setValidTo(e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Content */}
          <Card className="border-white/10">
            <CardHeader>
              <CardTitle className="text-lg">
                Contenido {isTemplate ? 'de la Plantilla' : 'del Contrato'}
              </CardTitle>
              {isTemplate && (
                <p className="text-sm text-muted-foreground">
                  Puedes usar variables como {'{{clientName}}'}, {'{{clientEmail}}'},{' '}
                  {'{{quoteCode}}'}, {'{{date}}'} que se reemplazarán al generar un contrato.
                </p>
              )}
            </CardHeader>
            <CardContent>
              <Textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Escribe el contenido del contrato en formato Markdown..."
                className="min-h-[400px] font-mono text-sm"
              />
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(isEditing ? `/contracts/${id}` : '/contracts')}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <Save className="h-4 w-4 mr-2" />
              {isEditing ? 'Guardar Cambios' : 'Crear'}
            </Button>
          </div>
        </form>
      </div>
    </AppLayout>
  );
}
